import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { IMAGES } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const CHAPTERS = [
  { n: "01", title: "Rooted in the Mud", text: "Every transformation begins in the mess. Cluttered garages, full estates, forgotten basements — the raw material of a fresh start." },
  { n: "02", title: "Growing Through the Water", text: "Our professional crew does the heavy lifting with care, hauling away what no longer serves you and disposing of it responsibly." },
  { n: "03", title: "Blooming Into Beauty", text: "What's left is a clean, functional, open space — a property reclaimed to its full potential. That's the Lotus promise." },
];

const points = [
  "Locally owned & operated in Chester, PA",
  "Fully insured for your protection",
  "Eco-friendly disposal & donations",
  "5-star service, every single time",
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-32 bg-[#111111]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        {/* Image */}
        <Reveal className="order-2 lg:order-1">
          <div className="relative">
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 aspect-[4/5] shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <motion.img
                src={IMAGES.crew}
                alt="Professional Lotus crew member"
                style={{ y }}
                className="absolute inset-0 h-[120%] w-full object-cover -top-[10%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/70 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:right-6 rounded-2xl bg-[#F56FB4] px-6 py-5 shadow-[0_0_40px_rgba(245,111,180,0.4)]">
              <p className="font-heading font-extrabold text-3xl text-white leading-none">100%</p>
              <p className="text-xs text-white/90 mt-1 font-medium">Satisfaction Focused</p>
            </div>
          </div>
        </Reveal>

        {/* Copy */}
        <div className="order-1 lg:order-2">
          <Reveal><Eyebrow>Why Lotus?</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-heading font-extrabold uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-3">
              Rooted in purpose.<br />
              <span className="text-[#F56FB4]">Driven by service.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mt-5 max-w-xl">
              The lotus rises from mud into something beautiful — and so can any space.
              We don&apos;t just remove junk. We help homes and businesses reclaim their
              potential, one transformation at a time.
            </p>
          </Reveal>

          <div className="mt-9 space-y-5">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.1 + i * 0.08}>
                <div className="flex gap-5 group">
                  <span className="font-heading font-extrabold text-2xl text-[#53C653] w-10 shrink-0 pt-0.5">{c.n}</span>
                  <div className="border-l border-white/10 pl-5 group-hover:border-[#F56FB4]/50 transition-colors duration-300">
                    <h3 className="font-heading font-bold text-xl text-white">{c.title}</h3>
                    <p className="text-white/60 text-sm md:text-base mt-1 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-9 grid sm:grid-cols-2 gap-3">
              {points.map((p) => (
                <div key={p} className="flex items-center gap-2.5">
                  <CheckCircle2 size={18} className="text-[#53C653] shrink-0" />
                  <span className="text-sm text-white/80">{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
